import { useEffect, useState, useCallback } from "react";
import { useLocation } from "wouter";
import { useT } from "../../lib/i18n/useT";
import { listConsultants, type ConsultantListEntry } from "../../lib/api/consultants-v2";
import ConsultantAvatar from "../shared/ConsultantAvatar";

// Consultants list, populated from the Entra tenant directory (enabled
// Member users only) LEFT JOIN core.consultants in Postgres. New hires
// appear automatically as soon as HR provisions their Entra account;
// offboarded staff drop off the primary list but remain visible if they
// still have Lens data (marked "Former").
export default function ConsultantsModule() {
  const t = useT();
  const [, navigate] = useLocation();
  const [rows, setRows] = useState<ConsultantListEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [showInactive, setShowInactive] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const list = await listConsultants();
      setRows(Array.isArray(list) ? list : []);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load consultants");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const visible = rows
    .filter((r) => showInactive || r.active)
    .filter((r) =>
      !search.trim() ||
      (r.displayName ?? "").toLowerCase().includes(search.toLowerCase()) ||
      (r.mail ?? "").toLowerCase().includes(search.toLowerCase()),
    );

  const inactiveCount = rows.filter((r) => !r.active).length;

  return (
    <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100 tracking-tight">
            {t("people.title")}
          </h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
            {visible.length} {visible.length === 1 ? "person" : "people"} in Entra directory
            {inactiveCount > 0 && !showInactive && (
              <span className="ml-2 text-slate-400">· {inactiveCount} inactive hidden</span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {inactiveCount > 0 && (
            <button
              onClick={() => setShowInactive((v) => !v)}
              className="rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-transparent px-3 py-1.5 text-xs font-semibold text-slate-500 dark:text-slate-400 transition-colors cursor-pointer focus:outline-none"
            >
              {showInactive ? "Hide inactive" : `Show inactive (${inactiveCount})`}
            </button>
          )}
          <button
            onClick={() => void load()}
            className="rounded-lg bg-slate-100 hover:bg-slate-200 border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-600 transition-colors cursor-pointer focus:outline-none"
          >
            ↻ Refresh
          </button>
        </div>
      </div>

      <div className="relative max-w-sm">
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search name or email…"
          className="w-full rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-[#131825] px-3.5 py-1.5 text-sm text-slate-800 dark:text-slate-200 placeholder-slate-400 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 focus:outline-none"
        />
      </div>

      {error && (
        <div className="rounded-lg border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700">
          <p className="font-semibold">Could not load consultants</p>
          <p className="text-xs mt-1">{error}</p>
          <p className="text-xs mt-1 text-rose-600">
            Check that <code>func-lens-core</code>'s managed identity has <code className="font-mono">User.Read.All</code> permission on Microsoft Graph (admin consent required).
          </p>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-24">
          <span className="h-7 w-7 animate-spin rounded-full border-2 border-slate-300 border-t-slate-800" />
        </div>
      ) : (
        <div className="rounded-2xl border border-slate-200/70 dark:border-slate-700/50 bg-white dark:bg-[#182132] shadow-xs overflow-hidden">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="border-b border-slate-100 dark:border-slate-700/60">
                {["Consultant", "Email", "Role", "In directory", "Status", "Seniority"].map((k) => (
                  <th key={k} className="py-3 px-4 text-[10px] font-bold uppercase tracking-wider text-slate-400 bg-slate-50/60 dark:bg-slate-800/40">
                    {k}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50 dark:divide-slate-700/30">
              {visible.length === 0 ? (
                <tr>
                  <td colSpan={6} className="py-12 text-center text-sm text-slate-400">
                    No consultants
                  </td>
                </tr>
              ) : (
                visible.map((r) => (
                  <tr
                    key={r.oid}
                    onClick={() => navigate(`/consultants/${encodeURIComponent(r.oid)}`)}
                    className={`cursor-pointer hover:bg-indigo-50/40 dark:hover:bg-indigo-900/10 transition-colors ${!r.active ? "opacity-50" : ""}`}
                  >
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-2.5">
                        <ConsultantAvatar name={r.displayName ?? "?"} />
                        <span className="text-sm font-semibold text-indigo-600 dark:text-indigo-400 hover:underline">
                          {r.displayName ?? "(no name)"}
                        </span>
                      </div>
                    </td>
                    <td className="py-3 px-4 text-sm text-slate-700 dark:text-slate-300">
                      {r.mail ?? <span className="text-slate-300">—</span>}
                    </td>
                    <td className="py-3 px-4 text-sm text-slate-600 dark:text-slate-400">
                      {r.jobTitle ?? <span className="text-slate-300">—</span>}
                    </td>
                    <td className="py-3 px-4">
                      {r.inDirectory ? (
                        <span className="inline-flex items-center gap-1 text-xs text-emerald-700 dark:text-emerald-400">
                          <span className="h-2 w-2 rounded-full bg-emerald-500" />
                          Yes
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-xs text-slate-500">
                          <span className="h-2 w-2 rounded-full bg-slate-300" />
                          Former
                        </span>
                      )}
                    </td>
                    <td className="py-3 px-4 text-sm">
                      <span className={r.active ? "text-slate-700 dark:text-slate-300" : "text-slate-400"}>
                        {r.active ? "Active" : "Inactive"}
                      </span>
                      {r.nonConsulting && (
                        <span className="ml-2 rounded-sm bg-slate-100 dark:bg-slate-700 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-slate-500">
                          Non-Consulting
                        </span>
                      )}
                    </td>
                    <td className="py-3 px-4 text-sm text-slate-600 dark:text-slate-400 capitalize">
                      {r.seniority ?? <span className="text-slate-300">—</span>}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
